import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BTN_LG } from '../constants/buttons';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatDate } from '../utils/dateFormat';
import { apiGet, apiPost } from '../utils/api';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

// Friendly copy for "already responded / no longer valid" states.
const RESOLVED_MESSAGES = {
  approved: 'This request has already been responded to.',
  rejected: 'This request has already been responded to.',
  withdrawn: 'This request has been withdrawn and is no longer active.',
  expired: 'This link has expired.',
};

export default function GradingReferenceApproval() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Match the public-page convention: force dark base styling on mount.
  useEffect(() => { document.documentElement.classList.add('dark'); }, []);

  const [validating, setValidating] = useState(true);
  const [loadError, setLoadError] = useState('');       // hard load failure (e.g. network)
  const [status, setStatus] = useState(null);           // 'pending' | resolved status
  const [data, setData] = useState(null);               // pending payload (applicant, block, etc.)

  const [approverName, setApproverName] = useState('');
  const [comment, setComment] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);           // 'approved' | 'rejected' after success

  // Load the request on mount.
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    apiGet(`/api/grading-approval/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setStatus('invalid');
          return;
        }
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setLoadError(body.error || 'Unable to load this approval request.');
          return;
        }
        setStatus(body.status);
        if (body.status === 'pending') {
          setData(body);
          setApproverName(body.referenceName || '');
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Unable to load this approval request. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const submitDecision = async (decision) => {
    if (submitting) return; // guard double-submit
    setFormError('');
    if (!approverName.trim()) {
      setFormError('Please enter your name.');
      return;
    }
    if (decision === 'rejected' && !comment.trim()) {
      setFormError('Please give a reason for declining.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPost(`/api/grading-approval/${encodeURIComponent(token)}`, {
        decision,
        approver_name: approverName.trim(),
        comment: comment.trim(),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult(body.status || decision);
        return;
      }
      if (res.status === 409) {
        // Already responded / expired elsewhere — re-fetch to show the resolved card.
        const refetch = await apiGet(`/api/grading-approval/${encodeURIComponent(token)}`);
        if (refetch.status === 404) {
          setStatus('invalid');
        } else {
          const refBody = await refetch.json().catch(() => ({}));
          setStatus(refBody.status || 'expired');
        }
        return;
      }
      setFormError(body.error || 'Something went wrong. Please try again.');
    } catch {
      setFormError('Unable to submit your response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Shared chrome -------------------------------------------------------
  const shell = (subtitle, inner) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            CREW FORGE
          </h1>
          {subtitle && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          {inner}
        </div>
      </div>
    </div>
  );

  const infoCard = (variant, title, message) => {
    const palette = variant === 'error'
      ? { bg: 'bg-red-100 dark:bg-red-900/30', icon: 'text-red-600 dark:text-red-400' }
      : { bg: 'bg-gray-100 dark:bg-gray-700', icon: 'text-gray-500 dark:text-gray-400' };
    return (
      <div className="text-center space-y-4">
        <div className={`w-12 h-12 mx-auto ${palette.bg} rounded-full flex items-center justify-center`}>
          <svg className={`w-6 h-6 ${palette.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
      </div>
    );
  };

  // --- Render states (hooks all declared above) ----------------------------
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner message="Loading approval request..." />
      </div>
    );
  }

  if (loadError) {
    return shell('Confirm SFX experience', infoCard('error', 'Something went wrong', loadError));
  }

  if (!token || status === 'invalid') {
    return shell('Confirm SFX experience', infoCard('error', 'Invalid link', 'This approval link is invalid.'));
  }

  if (result) {
    const verb = result === 'approved' ? 'approved' : 'declined';
    const applicant = data?.applicantName || 'the applicant';
    return shell('Confirm SFX experience', (
      <div className="text-center space-y-4">
        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${result === 'approved' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          <svg className={`w-6 h-6 ${result === 'approved' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {result === 'approved'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Thank you</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          You&apos;ve <span className="font-semibold text-gray-700 dark:text-gray-200">{verb}</span> this experience for {applicant}.
        </p>
      </div>
    ));
  }

  if (status !== 'pending') {
    const message = RESOLVED_MESSAGES[status] || 'This link is no longer valid.';
    return shell('Confirm SFX experience', infoCard('info', 'No action needed', message));
  }

  // --- Pending review UI ---------------------------------------------------
  const block = data.block || {};
  const refName = data.referenceName || '';
  const refTitle = data.referenceTitle ? `, ${data.referenceTitle}` : '';

  // Read-only summary rows — omit blank fields gracefully.
  const dateRange = (() => {
    if (block.start_date && block.end_date) return `${formatDate(block.start_date)} – ${formatDate(block.end_date)}`;
    if (block.start_date) return formatDate(block.start_date);
    if (block.end_date) return formatDate(block.end_date);
    return null;
  })();

  const rows = [
    ['Production', block.production_name],
    ['Dates', dateRange],
    ['SFX Supervisor', block.supervisor_name],
    ['Credited grade', block.credited_grade],
    ['Weeks worked', block.weeks_worked],
    ['Odd days', block.odd_days],
    ['Total days', block.total_days],
    ['Example of effects', block.effects_techniques],
    ['Notes', block.notes],
  ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');

  return shell('Confirm SFX experience', (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        <span className="font-semibold text-gray-900 dark:text-white">{data.applicantName}</span> has named you
        {' '}({refName}{refTitle}) as a reference for the following production experience and is asking you to confirm it.
      </p>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 divide-y divide-gray-200 dark:divide-gray-700">
        {rows.map(([label, value]) => (
          <div key={label} className="px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">{String(value)}</dd>
          </div>
        ))}
      </div>

      {formError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          {formError}
        </div>
      )}

      <div>
        <label htmlFor="approverName" className={FIELD_LABEL_CLASS}>
          Your name
        </label>
        <input
          id="approverName"
          type="text"
          required
          value={approverName}
          onChange={(e) => setApproverName(e.target.value)}
          disabled={submitting}
          className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60"
          placeholder="Your full name"
        />
      </div>

      <div>
        <label htmlFor="comment" className={FIELD_LABEL_CLASS}>
          Comment <span className="text-gray-400 dark:text-gray-500 font-normal">(required if declining)</span>
        </label>
        <textarea
          id="comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={submitting}
          className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-60"
          placeholder="Optional comment, or a reason if you're declining"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => submitDecision('approved')}
          disabled={submitting}
          className={`${BTN_LG.confirm} flex-1 justify-center`}
        >
          {submitting ? 'Submitting...' : 'Approve'}
        </button>
        <button
          type="button"
          onClick={() => submitDecision('rejected')}
          disabled={submitting}
          className={`${BTN_LG.danger} flex-1 justify-center`}
        >
          {submitting ? 'Submitting...' : 'Decline'}
        </button>
      </div>
    </div>
  ));
}
